import amplify from '@services/amplify';
import { config } from '@services/amplify/config';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import api from './api';

const prefixResolver = ({ accessLevel, targetIdentityId }: { accessLevel: string; targetIdentityId: string }) => {
  if (accessLevel === 'guest') {
    return 'amplify/public/';
  } else if (accessLevel === 'protected') {
    return `amplify/protected/${targetIdentityId}/`;
  } else if (accessLevel === 'private') {
    return `amplify/private/${targetIdentityId}/`;
  } else {
    throw new Error(`Unknown access level: ${accessLevel}`);
  }
};

export const get = async (key: string) => {
  return (await getUrl({ path: key })).url.href;
};

export const put = async (key: string, object: any, options?: any): Promise<string | null> => {
  const response = await uploadData({
    data: object,
    options: options,
    path: ({ identityId }) =>
      prefixResolver({ accessLevel: options.level, targetIdentityId: identityId as string }) + key,
  }).result;
  return response?.path ?? null;
};

export async function uploadImage(image: File) {
  const splitName = image.name.split('.');
  const ext = splitName[splitName.length - 1];

  const key = await put(`sfr-${uuidv4()}.${ext}`, image, { contentType: 'image', level: 'private' });
  return { bucket: config?.Storage?.S3.bucket, key };
}

async function saveSignature(blob: Blob) {
  // save signature to storage
  const key = await put('signature-' + uuidv4(), blob, {
    contentType: 'image/png',
    level: 'private',
  });

  if (!key) {
    throw new Error('Failed to save signature');
  }

  // save signature to api
  const result = api.storeSignatureFile({
    signature: {
      bucket: config?.Storage?.S3.bucket as string,
      key,
      region: config?.Storage?.S3.region as string,
    },
  });

  return result;
}

async function saveHeadshot(file: File): Promise<{ success: boolean; error?: string }> {
  try {
    const uniqueId = uuidv4();

    // save headshot to storage
    const key = await put('profile-photo/' + uniqueId, file, {
      contentType: file.type,
      level: 'protected',
    });

    if (!key) {
      throw new Error('Failed to save headshot');
    }

    // save headshot to api
    const headshot = await api.storeHeadshot({
      photo: {
        bucket: config?.Storage?.S3.bucket as string,
        key,
        region: config?.Storage?.S3.region as string,
      },
    });

    if (!headshot) throw new Error('Failed to store headshot. Please try again later.');

    return {
      success: true,
    };
  } catch (err: any) {
    return {
      error: err.message,
      success: false,
    };
  }
}

let hasInitialized = false;
export default (() => {
  if (!hasInitialized) {
    amplify; // auto-initialize s3
    hasInitialized = true;
  }

  return { saveHeadshot, saveSignature };
})();
